/*
 * Evolution Design: shows the customer's uploaded photo on the wall in the main product picture.
 *
 * The photo upload app (Uploadly) adds a thumbnail of each uploaded file under #photouploadapp. This script watches for
 * that thumbnail and lays the same image over the print hanging above the sofa in the main product photo.
 * Loaded only on the custom-photo product template (see layout/theme.liquid).
 */
(() => {
  // The main product photo with the print on the wall, and where that print sits in it (percent of the photo).
  // If the main photo is replaced, measure the new print and update these numbers.
  const MEDIA_ID = '37183238832293';
  const FRAME = { left: 21.3, top: 11.7, width: 57.7, height: 35.4 };

  const LABEL = 'Your photo';
  const JUMP_LABEL = 'See it on the wall';

  const findMedia = () => document.querySelector(`.product-gallery__media[data-media-id="${MEDIA_ID}"]`);

  // The newest thumbnail the upload app shows, skipping its placeholder for files it cannot preview.
  const latestUpload = () => {
    const thumbnails = document.querySelectorAll('#photouploadapp [id^="previewImages_"] img');
    for (let index = thumbnails.length - 1; index >= 0; index--) {
      const src = thumbnails[index].getAttribute('src') || '';
      if (/^https?:\/\//.test(src) && !src.includes('dummyimage.com')) return src;
    }
    return null;
  };

  // The upload app's thumbnail is shrunk to 500px. For Uploadcare files, drop that resize (keeping any crop the customer
  // made) and ask for a sharper copy sized for the main picture; anything else is used as is.
  const lighter = (url) => {
    if (!/ucarecdn\.com|ucarecd\.net/.test(url)) return url;
    const base = url.replace(/-\/(resize|preview|scale_crop|quality|format)\/[^/]*\//g, '').replace(/([^:])\/{2,}/g, '$1/');
    return base.replace(/\/?$/, '/') + '-/preview/1600x1600/-/quality/smart/';
  };

  const buildLayer = (media) => {
    const layer = document.createElement('div');
    layer.className = 'photo-preview';
    layer.hidden = true;
    layer.style.setProperty('--frame-left', `${FRAME.left}%`);
    layer.style.setProperty('--frame-top', `${FRAME.top}%`);
    layer.style.setProperty('--frame-width', `${FRAME.width}%`);
    layer.style.setProperty('--frame-height', `${FRAME.height}%`);
    layer.innerHTML = `<div class="photo-preview__print"><img alt="${LABEL} on the wall" decoding="async"></div><span class="photo-preview__badge">${LABEL}</span>`;
    media.appendChild(layer);
    return layer;
  };

  const buildJumpLink = () => {
    const app = document.getElementById('photouploadapp');
    if (!app) return null;
    let link = app.parentElement.querySelector('.photo-preview-jump');
    if (!link) {
      link = document.createElement('button');
      link.type = 'button';
      link.className = 'photo-preview-jump link text-sm';
      link.textContent = JUMP_LABEL;
      link.hidden = true;
      link.addEventListener('click', () => {
        const media = findMedia();
        if (media) media.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      app.after(link);
    }
    return link;
  };

  let pending = false;

  const render = () => {
    pending = false;
    const media = findMedia();
    if (!media) return;

    const url = latestUpload();
    let layer = media.querySelector('.photo-preview');
    const link = buildJumpLink();

    if (!url) {
      if (layer) layer.remove();
      if (link) link.hidden = true;
      return;
    }

    if (!layer) layer = buildLayer(media);
    if (layer.dataset.source === url) return;
    layer.dataset.source = url;

    const image = layer.querySelector('img');
    image.onload = () => {
      layer.hidden = false;
      if (link) link.hidden = false;
    };
    image.onerror = () => {
      // The lighter copy failed (for example a PDF): try the original once, then give up quietly.
      if (image.dataset.fallback !== url) {
        image.dataset.fallback = url;
        image.src = url;
      } else {
        layer.hidden = true;
        if (link) link.hidden = true;
      }
    };
    image.src = lighter(url);
  };

  const schedule = () => {
    if (pending) return;
    pending = true;
    window.setTimeout(render, 60);
  };

  const start = () => {
    // The upload app and the theme both rebuild parts of the page, so watch the whole page and re-check cheaply.
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
    schedule();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
